import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  Grid,
  InputAdornment,
  InputLabel,
  Stack,
  TextField,
  Typography,
  CircularProgress,
} from "@mui/material";
import MailOutline from "@mui/icons-material/MailOutline";
import Password from "@mui/icons-material/LockOutlined";
import { useNavigate } from "react-router-dom";
import { ZyppUploadApi } from "../../../api/ZyppUpload";
// import { OrderOnDemandApi } from "../API/ZyppIt";

const Login = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpMessage, setOtpMessage] = useState<string | null>(null);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [loader, setLoader] = useState(false);
  const [otpLoader, setOtpLoader] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const cooldownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const otpRefs = useRef<Array<HTMLInputElement | null>>(Array(6).fill(null));
  const navigate = useNavigate();

  useEffect(() => {
    return () => {
      if (cooldownRef.current) clearInterval(cooldownRef.current);
    };
  }, []);

  const startCooldown = () => {
    setResendCooldown(30);
    cooldownRef.current = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(cooldownRef.current!);
          cooldownRef.current = null;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleSubmit = (e: any) => {
    e.preventDefault();
    if (!otpSent) {
      handleSendOtp();
      return;
    }
    handleLogin();
  };

  const handleSendOtp = async () => {
    if (!username || !password) {
      setLoginError("Please enter username and password to receive OTP");
      return;
    }

    setOtpLoader(true);
    setLoginError(null);
    setOtpMessage(null);
    try {
      await ZyppUploadApi.api.sendDashboardOtp(username, password);
      setOtpSent(true);
      setOtpMessage("OTP sent successfully. Please enter OTP to continue.");
      startCooldown();
    } catch (error) {
      console.error("Error sending OTP:", error);
      setLoginError("Unable to send OTP. Please verify credentials and try again.");
    } finally {
      setOtpLoader(false);
    }
  };

  const handleLogin = async () => {
    if (!otp) {
      setLoginError("Please enter OTP");
      return;
    }

    setLoader(true);
    setLoginError(null);
    try {
      const response = await ZyppUploadApi.api.onlineUserAuthentication(
        username,
        password,
        otp,
      );

      if (
        response.data &&
        response.data.token &&
        response.data.role === "ROLE_DATA"
      ) {
        localStorage.setItem("token", response.data.token);
        navigate("/dailyPayout");
      } else {
        setLoginError(
          "Access Denied: You do not have permission to access this page."
        );
      }
    } catch (error) {
      console.error("Error:", error);
      setLoginError("Invalid OTP, Credentials, or Network Error");
    } finally {
      setLoader(false);
    }
  };

  if (
    loginError ===
    "Access Denied: You do not have permission to access this page."
  ) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Typography variant="h5" color="error">
          {loginError}
        </Typography>
      </div>
    );
  }

  return (
    <>
      <div className="flex justify-center">
        <form onSubmit={handleSubmit} className="w-2/5 pt-16">
          <Grid container spacing={3} className="w-full flex">
            <Grid item xs={12}>
              <p className="text-center font-bold text-3xl text-gray-500">
                Welcome!
              </p>
              <p className="text-center mb-8 mt-2 text-gray-500">
                Log In to your account
              </p>
              {!otpSent && (
              <Stack spacing={1}>
                <InputLabel className="text-left	" htmlFor="email">
                  Enter Username
                </InputLabel>
                <TextField
                  fullWidth
                  id="emailInstant"
                  name="emailInstant"
                  placeholder="Enter email address"
                  className="text-slate-300"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment
                        position="start"
                        className="text-slate-300"
                      >
                        <MailOutline />
                      </InputAdornment>
                    ),
                  }}
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                />
              </Stack>
              )}
            </Grid>
            {!otpSent && (
            <Grid item xs={12}>
              <Stack spacing={1}>
                <InputLabel className="text-left" htmlFor="email">
                  Enter Password
                </InputLabel>
                <TextField
                  fullWidth
                  id="passwordInstant"
                  name="passwordInstant"
                  placeholder="Enter your password"
                  type="password"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Password />
                      </InputAdornment>
                    ),
                  }}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </Stack>
            </Grid>
            )}
            {otpSent && (
              <Grid item xs={12}>
                <Stack spacing={1}>
                  <InputLabel className="text-left" htmlFor="otp-0">
                    Enter OTP
                  </InputLabel>
                  <Box 
                    sx={{ display: "flex", gap: 1 }}
                    onPaste={(e) => {
                      e.preventDefault();
                      const paste = (e.clipboardData?.getData("text") || "").replace(/\D/g, "").slice(0, 6);
                      setOtp(paste);
                      if (paste.length === 6) {
                        otpRefs.current[5]?.focus();
                      } else if (paste.length > 0) {
                        otpRefs.current[paste.length]?.focus();
                      }
                    }}
                  >
                    {Array.from({ length: 6 }).map((_, i) => (
                      <TextField
                        key={i}
                        id={i === 0 ? "otp-0" : undefined}
                        value={otp[i] || ""}
                        inputRef={(el) => { otpRefs.current[i] = el; }}
                        onChange={(e) => {
                          const digit = e.target.value.replace(/\D/g, "").slice(-1);
                          const arr = otp.split("");
                          arr[i] = digit;
                          const next = arr.join("");
                          setOtp(next);
                          if (digit && i < 5) otpRefs.current[i + 1]?.focus();
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Backspace" && !otp[i] && i > 0) {
                            otpRefs.current[i - 1]?.focus();
                          }
                        }}
                        inputProps={{
                          maxLength: 1,
                          inputMode: "numeric",
                          style: { textAlign: "center", fontSize: "1.4rem", padding: "10px 0" },
                        }}
                        sx={{ flex: 1 }}
                      />
                    ))}
                  </Box>
                  <Typography variant="body2" align="right">
                    {resendCooldown > 0 ? (
                      <span style={{ color: "#888" }}>Resend OTP in {resendCooldown}s</span>
                    ) : (
                      <span
                        onClick={otpLoader ? undefined : handleSendOtp}
                        style={{
                          color: "#00CD5B",
                          cursor: otpLoader ? "not-allowed" : "pointer",
                          fontWeight: 600,
                        }}
                      >
                        Resend OTP
                      </span>
                    )}
                  </Typography>
                </Stack>
              </Grid>
            )}
            {/* <Grid item xs={12} className="flex justify-end pt-0">
            <Typography variant="subtitle1">
              <a
                className="no-underline hover:underline text-[#0079FF] font-bold text-sm"
                href="/forgot-password"
              >
                Forgot Password?
              </a>
            </Typography>
          </Grid> */}
            <Grid item xs={12} style={{ marginTop: "28px" }}>
              {loader || otpLoader ? (
                <Button
                  sx={{
                    backgroundColor: "#00CD5B",
                    "&:hover": {
                      backgroundColor: "#63dd99",
                    },
                  }}
                  className="bg-[#00CD5B] hover:bg-[#63dd99]"
                  fullWidth
                  size="large"
                  variant="contained"
                  type="submit"
                >
                  <CircularProgress sx={{ color: "white" }} />
                </Button>
              ) : (
                <Button
                  sx={{
                    backgroundColor: "#00CD5B",
                    "&:hover": {
                      backgroundColor: "#63dd99",
                    },
                  }}
                  className="bg-[#00CD5B] hover:bg-[#63dd99]"
                  fullWidth
                  size="large"
                  variant="contained"
                  type="submit"
                >
                  {otpSent ? "Submit OTP" : "Send OTP"}
                </Button>
              )}
            </Grid>
            {otpMessage && (
              <Grid item xs={12}>
                <Typography variant="body1" color="success.main">
                  {otpMessage}
                </Typography>
              </Grid>
            )}
            {loginError && (
              <Grid item xs={12}>
                <Typography variant="body1" color="error">
                  {loginError}
                </Typography>
              </Grid>
            )}
          </Grid>
        </form>
      </div>
    </>
  );
};

export default Login;
